
import React from "react";
import styled, { keyframes } from "styled-components";

import ai from "../img/AODG26844.png";

const pulse = keyframes`
  0%, 100% { opacity: .25; transform: scale(1); }
  50% { opacity: .5; transform: scale(1.15); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const Section = styled.section`
  width: 100%;
  position: relative;
  overflow: hidden;
  direction: rtl;
  padding: 85px 22px;

  background:
    radial-gradient(ellipse at 80% 20%, rgba(56,189,248,.13), transparent 35%),
    linear-gradient(145deg, #020b19, #081a2f 55%, #06152a);

  @media(max-width: 600px) {
    padding: 55px 15px;
  }
`;

const Glow = styled.div`
  position: absolute;
  width: 380px;
  height: 380px;
  border-radius: 50%;
  background: #38bdf8;
  filter: blur(140px);
  right: 5%;
  top: 15%;
  opacity: .3;
  animation: ${pulse} 6s ease-in-out infinite;
  pointer-events: none;
`;

const Wrapper = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1180px;
  margin: auto;

  display: grid;
  grid-template-columns: 1fr 0.75fr;
  align-items: center;
  gap: 55px;

  @media(max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 35px;
  }
`;

const TextBox = styled.div`
  padding: 38px;
  border-radius: 22px;

  background: rgba(255,255,255,.055);
  border: 1px solid rgba(56,189,248,.3);

  box-shadow: 0 20px 55px rgba(0,0,0,.3);
  backdrop-filter: blur(15px);

  @media(max-width: 600px) {
    padding: 24px 19px;
    border-radius: 17px;
  }
`;

const Label = styled.div`
  display: inline-block;
  color: #38bdf8;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 4px;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  color: #fff;
  font-weight: 900;
  margin: 0 0 20px;

  span {
    color: #38bdf8;
    text-shadow: 0 0 20px rgba(56,189,248,.4);
  }
`;

const Intro = styled.p`
  color: #fff;
  font-size: 19px;
  font-weight: 800;
  margin: 0 0 17px;
`;

const Text = styled.p`
  color: #dbeafe;
  font-size: 15px;
  line-height: 1.95;
  margin: 0 0 18px;

  &:last-child {
    margin-bottom: 0;
  }

  @media(max-width: 600px) {
    font-size: 14px;
    line-height: 1.9;
  }
`;

const SubTitle = styled.h3`
  color: #38bdf8;
  font-size: 17px;
  font-weight: 850;
  margin: 25px 0 14px;

  &::after {
    content: "";
    display: block;
    width: 38px;
    height: 2px;
    background: #38bdf8;
    margin-top: 8px;
  }
`;

const FeatureList = styled.div`
  display: grid;
  gap: 11px;
`;

const Feature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: #e2e8f0;
  font-size: 14px;
  line-height: 1.8;

  .check {
    width: 23px;
    height: 23px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #38bdf8;
    background: rgba(56,189,248,.12);
    font-weight: 900;
    margin-top: 1px;
  }

  strong {
    color: #fff;
  }
`;

const ImageBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    width: 85%;
    aspect-ratio: 1;
    border: 1px solid rgba(56,189,248,.35);
    border-radius: 50%;
    box-shadow: 0 0 60px rgba(56,189,248,.12);
  }

  img {
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    max-width: 390px;
    max-height: 440px;
    object-fit: contain;
    filter: drop-shadow(0 0 35px rgba(56,189,248,.4));
    animation: ${float} 5s ease-in-out infinite;
  }

  @media(max-width: 850px) {
    grid-row: 1;

    img {
      max-width: 260px;
      max-height: 290px;
    }
  }

  @media(max-width: 480px) {
    img {
      max-width: 210px;
      max-height: 230px;
    }
  }
`;

const Signature = styled.div`
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid rgba(56,189,248,.2);

  color: #38bdf8;
  font-size: 14px;
  font-weight: 800;
`;

export function About() {
  return (
    <Section>
      <Glow />

      <Wrapper>
        <TextBox>

          <Title id="אודות">
            אודות <span>החברה</span>
          </Title>

          <Intro>
             אומנות הניקיון והתחזוקה
          </Intro>

          <Text>
            חברת הניקיון בהנהלת שי גדפור מביאה לשטח שנות מוניטין,
            ניסיון עשיר וצוות מיומן, המספקים פתרונות ניקיון מתקדמים
            בסטנדרט בלתי מתפשר. אנו מתמחים בהפיכת כל חלל — פרטי,
            עסקי או ציבורי — לסביבה נקייה, מבריקה ומזמינה.
          </Text>

          <SubTitle>המומחיות שלנו בשטח</SubTitle>

          <FeatureList>
            <Feature>
              <span className="check">✓</span>
              <span>
                <strong>פרויקטים לאחר שיפוץ : </strong>
                הסרת אבק בנייה, שאריות צבע, דבקים ורובה.
              </span>
            </Feature>
        <Feature>
              <span className="check">✓</span>
              <span>
                <strong> קרצוף, פוליש ווקס לרצפות : </strong>
מחזירים לרצפה הישנה את המראה החדש.           </span>
            </Feature>
            <Feature>
              <span className="check">✓</span>
              <span>
                <strong>אחזקת משרדים ושטחי מסחר: </strong>
                יצירת סביבת עבודה פרודוקטיבית, ייצוגית והיגיינית.
              </span>
            </Feature>

            <Feature>
              <span className="check">✓</span>
              <span>
                <strong>בנייני ציבור ומוסדות : </strong>
                ניהול מערכי ניקיון מורכבים בחללים רחבי ידיים.
              </span>
            </Feature>
          </FeatureList>

          <SubTitle>החתימה המקצועית שלנו</SubTitle>

          <FeatureList>
            <Feature>
              <span className="check">✓</span>
              <span>
                <strong>טכנולוגיית קצה : </strong>
                שימוש במכשור תעשייתי מתקדם ובחומרי ניקיון מובילים.
              </span>
            </Feature>

            <Feature>
              <span className="check">✓</span>
              <span>
                <strong>ניהול קפדני : </strong>
                עמידה בלוחות זמנים, שקיפות מלאה וליווי אישי מול שי גדפור.
              </span>
            </Feature>

            <Feature>
              <span className="check">✓</span>
              <span>
                <strong>אחריות ושירות : </strong>
                מחירים הוגנים והתחייבות לאיכות ביצוע בכל פרויקט.
              </span>
            </Feature>
          </FeatureList>

          <SubTitle>למה לקוחות בוחרים בנו?</SubTitle>

          <Text>
            ניסיון וותק, אמינות, יחס אישי, אחריות ועמידה בזמנים.
            אנחנו מאמינים בשירות מקצועי ובתוצאות שמדברות בעד עצמן
          </Text>


        </TextBox>

        <ImageBox>
          <img src={ai} alt="S.G1 - ניקיון ואחזקת מבנים" />
        </ImageBox>
      </Wrapper>
    </Section>
  );
}

export default About;
